import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, Search, FolderOpen } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useAdminAllProjects } from "@/hooks/use-admin";
import { formatNumber } from "@/lib/utils";

export const Route = createFileRoute("/_auth/admin/projects")({
  component: AdminProjectsPage,
});

function AdminProjectsPage() {
  const [q, setQ] = useState("");
  const projects = useAdminAllProjects({ q: q || undefined });

  return (
    <div className="px-6 py-6 max-w-[1480px] mx-auto">
      <header className="mb-6">
        <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-ink-500 hover:text-ink-700 mb-2">
          <ArrowLeft size={14} /> Volver
        </Link>
        <h1 className="text-2xl font-bold text-ink-900 dark:text-white">Proyectos</h1>
        <p className="text-sm text-ink-500 mt-1">Todos los proyectos de la plataforma, con su tenant asociado.</p>
      </header>

      <Card className="mb-4">
        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
          <Input
            className="pl-10"
            placeholder="Buscar por nombre de proyecto…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Proyectos</CardTitle>
          <CardDescription>
            {projects.data ? `${projects.data.length} resultados` : "Cargando…"}
          </CardDescription>
        </CardHeader>
        <CardContent className="divide-y divide-ink-100 dark:divide-ink-800">
          {projects.isLoading ? (
            Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-16 mb-2" />)
          ) : !projects.data || projects.data.length === 0 ? (
            <p className="text-sm text-ink-500 py-6 text-center">Sin proyectos.</p>
          ) : (
            projects.data.map((p) => (
              <div key={p.id} className="py-3 flex items-center gap-3">
                <FolderOpen size={18} className="text-ink-400 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-ink-900 dark:text-white">{p.name}</span>
                    <Badge variant="muted">{formatNumber(p.mentions_count)} menciones</Badge>
                  </div>
                  <p className="text-xs text-ink-500 mt-1">
                    <Link
                      to="/admin/accounts/$accountId"
                      params={{ accountId: String(p.account_id) }}
                      className="text-brand-600 hover:underline"
                    >
                      {p.account_name}
                    </Link>
                    {" · creado "}
                    {new Date(p.created_at).toLocaleDateString("es-PE")}
                    {p.description && ` · ${p.description.slice(0, 80)}`}
                  </p>
                </div>
                <span className="text-[11px] text-ink-500 font-mono shrink-0">#{p.id}</span>
              </div>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
}
